'use client'

import { differenceInMinutes } from 'date-fns'
import * as React from 'react'

export type TPersistedOrder = {
  /**
   * an order id
   */
  id: ID
  /**
   * a date when the order was created
   */
  createdAt: Date
  /**
   * a user token
   */
  authToken: Nullable<string>
}

type TSetOrderOptions = {
  /**
   * an order id
   */
  id: ID
  /**
   * a user token
   */
  userToken: Nullable<string>
}

type TClearOrder = () => void

type TProps = {
  /**
   * an order to be persisted
   */
  order?: Nullable<TPersistedOrder>
  /**
   * a function to set the order
   */
  setOrder: (updatedData: TPersistedOrder) => void
  /**
   * a function to reset the order
   */
  resetOrder: () => void
  /**
   * a function to clear the order
   */
  onOrderClear?: () => void
  /**
   * a function to reset the persistence
   */
  resetPersistence: (clearOrder: TClearOrder) => void
}

const ORDER_EXPIRATION_MINUTES = 60

/**
 * Handles persisting of the `orderId`:
 *  - Provides `orderId`.
 *  - Provides functions to store/delete the orderId with `setOrderId`/`clearOrder`.
 *  - If the `orderId` is too old resets all persistence and reloads the window.
 */
export const usePersistedOrderId = ({
  order,
  setOrder,
  resetOrder,
  onOrderClear,
  resetPersistence,
}: TProps) => {
  const setOrderId = React.useCallback(
    ({ id, userToken }: TSetOrderOptions) => {
      setOrder({ id, createdAt: new Date(), authToken: userToken })
    },
    [setOrder],
  )

  const updateOrderToken = React.useCallback(
    (userToken: TSetOrderOptions['userToken']) => {
      if (!order) return

      setOrder({ ...order, authToken: userToken })
    },
    [order, setOrder],
  )

  const clearOrder = React.useCallback(() => {
    // remove persisted order
    resetOrder()
    // run the provided action if defined
    onOrderClear?.()
  }, [onOrderClear, resetOrder])

  // Resets the order that is too old and the customer has not finished it yet.
  React.useEffect(() => {
    if (!order) return

    const now = new Date()
    const createdAt = new Date(order.createdAt)

    const isExpired = differenceInMinutes(now, createdAt) > ORDER_EXPIRATION_MINUTES

    // to maintain the backward compatibility - we stored only the `orderId` before
    // we want to reset the persistence in such case
    // this condition may be removed later if it is not needed anymore
    const isObsolete = typeof order === `string`

    if (isExpired || isObsolete) {
      resetPersistence(clearOrder)
    }
  }, [clearOrder, order, resetPersistence])

  const orderId = React.useMemo(() => order?.id ?? null, [order])
  const orderAuthToken = React.useMemo(() => order?.authToken ?? null, [order])

  return {
    orderId,
    setOrderId,
    clearOrder,
    updateOrderToken,
    orderAuthToken,
    isLoadingPersistence: order === undefined,
  }
}
